import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Smartphone, Monitor, Apple } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/AppShell";
import { LanguageSelect } from "@/components/LanguageSelect";
import { ImportCard } from "@/components/ImportCard";
import { api } from "@/lib/api";
import { useErrorToast } from "@/lib/hooks";
import { useAuth, useWs } from "@/context/Auth";
import { useI18n } from "@/i18n/I18n";

const Card = ({ title, children, testId }) => (
  <section data-testid={testId} className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-4">
    <h2 className="text-lg font-bold">{title}</h2>{children}
  </section>
);

const InstallCard = () => {
  const { t } = useI18n();
  const [prompt, setPrompt] = useState(null);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  return (
    <Card title={t("set.install")} testId="install-card">
      <ul className="space-y-3 text-sm text-slate-600">
        <li className="flex gap-3"><Apple className="w-4 h-4 mt-0.5 shrink-0" />{t("set.installIos")}</li>
        <li className="flex gap-3"><Smartphone className="w-4 h-4 mt-0.5 shrink-0" />{t("set.installAndroid")}</li>
        <li className="flex gap-3"><Monitor className="w-4 h-4 mt-0.5 shrink-0" />{t("set.installDesktop")}</li>
      </ul>
      {prompt && <Button data-testid="install-now-button" onClick={() => prompt.prompt()} className="h-11">{t("set.installNow")}</Button>}
    </Card>
  );
};

export default function Settings() {
  const { t } = useI18n();
  const { me, refresh } = useAuth();
  const { ws, canManage, canWrite } = useWs();
  const showErr = useErrorToast();
  const [name, setName] = useState(me.user.name);
  const [wsName, setWsName] = useState(ws.name);

  const save = (fn) => async (e) => {
    e.preventDefault();
    try { await fn(); await refresh(); toast.success(t("common.saved")); } catch (err) { showErr(err); }
  };
  const exportXlsx = async () => {
    try {
      const { data } = await api.get("/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: "rangeo-inventaire.xlsx" });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { showErr(e); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader title={t("set.title")} />
      <Card title={t("set.language")} testId="settings-language"><LanguageSelect persist className="w-full sm:w-64" /></Card>
      <Card title={t("set.profile")} testId="settings-profile">
        <form onSubmit={save(() => api.put("/auth/profile", { name }))} className="flex gap-2">
          <Input data-testid="settings-name-input" className="h-12" value={name} onChange={(e) => setName(e.target.value)} required />
          <Button data-testid="settings-name-save" type="submit" className="h-12">{t("common.save")}</Button>
        </form>
        <p className="text-sm text-slate-500">{me.user.email}</p>
      </Card>
      {canManage && (
        <Card title={t("set.workspace")} testId="settings-workspace">
          <form onSubmit={save(() => api.put("/workspace", { name: wsName, type: ws.type }))} className="space-y-1.5">
            <Label>{t("onb.wsName")}</Label>
            <div className="flex gap-2">
              <Input data-testid="settings-ws-input" className="h-12" value={wsName} onChange={(e) => setWsName(e.target.value)} required />
              <Button data-testid="settings-ws-save" type="submit" className="h-12">{t("common.save")}</Button>
            </div>
          </form>
        </Card>
      )}
      <Card title={t("set.export")} testId="settings-export">
        <p className="text-sm text-slate-500">{t("set.exportDesc")}</p>
        <Button data-testid="export-button" variant="outline" className="h-12 gap-2" onClick={exportXlsx}><Download className="w-4 h-4" />{t("set.export")}</Button>
      </Card>
      {canWrite && <ImportCard />}
      <InstallCard />
    </div>
  );
}
